'use client'

import { createContext, useCallback, useMemo, useState } from 'react'

import { getConversationsStats } from '../api'
import { UnreadMessagesContextValue, UnreadMessagesProviderProps } from '../types/unread-messages'

export const UnreadMessagesContext = createContext<UnreadMessagesContextValue>({
  unreadMsgCount: undefined,
  setUnreadMsgCount: () => {
    throw new Error('setUnreadMsgCount must be provided in UnreadMessagesProvider')
  },
  fetchUnreadMsgCount: () => {
    throw new Error('fetchUnreadMsgCount must be provided in UnreadMessagesProvider')
  },
})

const UnreadMessagesProvider = ({
  children,
  userId,
  initialUnreadMsgCount,
}: UnreadMessagesProviderProps) => {
  const [unreadMsgCount, setUnreadMsgCount] = useState<number | undefined>(initialUnreadMsgCount)

  const handleApiResponse = useCallback(
    (response: Awaited<ReturnType<typeof getConversationsStats>>) => {
      if ('errors' in response) return

      setUnreadMsgCount(response.unread_msg_count)
    },
    [],
  )

  const fetchUnreadMsgCount = useCallback(() => {
    if (!userId) return

    getConversationsStats().then(handleApiResponse)
  }, [userId, handleApiResponse])

  const contextValues = useMemo(
    () => ({
      unreadMsgCount,
      setUnreadMsgCount,
      fetchUnreadMsgCount,
    }),
    [unreadMsgCount, setUnreadMsgCount, fetchUnreadMsgCount],
  )

  return (
    <UnreadMessagesContext.Provider value={contextValues}>
      {children}
    </UnreadMessagesContext.Provider>
  )
}

export default UnreadMessagesProvider
